import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Play, Pause, Undo2, Redo2, Loader2, Film, Smartphone, Scissors,
  Captions, Palette, Sparkles, ClipboardCheck, AlertTriangle, Video,
  Clapperboard, Layers, ListChecks, Upload, Link2, ShieldCheck, Plus, Type,
  ChevronsUp, ChevronsDown, ArrowLeft, Flag, X, Pencil, Trash2,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

import { EditorTimelineRuler } from '@/components/editor/EditorTimelineRuler';
import { EditorClipTrimControls } from '@/components/editor/EditorClipTrimControls';
import { EditorClipList } from '@/components/editor/EditorClipList';
import { EditorCaptionsPanel } from '@/components/editor/EditorCaptionsPanel';
import { EditorBrandKitPanel } from '@/components/editor/EditorBrandKitPanel';
import { EditorAiPanel } from '@/components/editor/EditorAiPanel';
import { EditorReviewPanel } from '@/components/editor/EditorReviewPanel';
import { EditorMediaPanel } from '@/components/editor/EditorMediaPanel';
import { EditorPreviewPlayer } from '@/components/editor/EditorPreviewPlayer';
import { EditorInspectorPanel } from '@/components/editor/EditorInspectorPanel';
import { EditorEditingToolbar } from '@/components/editor/EditorEditingToolbar';
import { EditorMultiTrackTimeline } from '@/components/editor/EditorMultiTrackTimeline';
import { EditorClipReviewQueue } from '@/components/editor/EditorClipReviewQueue';
import { EditorToolSidebar } from '@/components/editor/EditorToolSidebar';
import { EditorPlaceholderPanel } from '@/components/editor/EditorPlaceholderPanel';
import { EditorTemplatesPanel } from '@/components/editor/EditorTemplatesPanel';
import { EditorBrandDefaults } from '@/components/editor/EditorBrandDefaults';
import { EditorAiToolsPanel } from '@/components/editor/EditorAiToolsPanel';
import { EditorMasterApprovalPanel } from '@/components/editor/EditorMasterApprovalPanel';
import { EditorClipGenerationPanel } from '@/components/editor/EditorClipGenerationPanel';
import { EditorEffectsCatalogPanel } from '@/components/editor/EditorEffectsCatalogPanel';
import { EditorTransitionsCatalogPanel } from '@/components/editor/EditorTransitionsCatalogPanel';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ProductionToolsDropdown } from '@/components/production/ProductionToolsDropdown';

const TOOL_CATEGORY_LABELS = {
  media: 'Media', audio: 'Audio', text: 'Text', captions: 'Captions',
  effects: 'Effects', transitions: 'Transitions', brand: 'Brand Kit',
  ai: 'AI Tools', templates: 'Templates',
};

const API_URL = window.__BACKEND_URL;
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

const clampCaptionsToDuration = (captions, duration) =>
  captions
    .filter((c) => c.start < duration)
    .map((c) => ({ ...c, end: Math.min(c.end, duration) }));

// ── Master composition defaults (Stage 3A) ──
// Default lane set matching the approved layout (V3/V2/V1 video — higher
// order = higher visual layer — then A1/A2 audio, then T1 text). Users can
// add further tracks at runtime (see addTrack in UnifiedEditor) — new
// tracks are flagged `custom: true` so only empty, user-added tracks can
// be removed; these six defaults are always kept.
const DEFAULT_MASTER_TRACKS = [
  { id: 'track-v3', type: 'video', order: 6, name: 'V3', locked: false, muted: false, hidden: false },
  { id: 'track-v2', type: 'video', order: 5, name: 'V2', locked: false, muted: false, hidden: false },
  { id: 'track-v1', type: 'video', order: 4, name: 'V1', locked: false, muted: false, hidden: false },
  { id: 'track-a1', type: 'audio', order: 3, name: 'A1', locked: false, muted: false, hidden: false },
  { id: 'track-a2', type: 'audio', order: 2, name: 'A2', locked: false, muted: false, hidden: false },
  { id: 'track-t1', type: 'text', order: 1, name: 'T1', locked: false, muted: false, hidden: false },
];
const defaultTransform = () => ({ scale_percent: 100, position_x: 0, position_y: 0, rotation_degrees: 0, opacity_percent: 100 });
const defaultAudioProps = () => ({ volume_percent: 100, muted: false });
const MIN_MASTER_ITEM_DURATION = 0.3;

const seedMasterComposition = (contentSource, sourceDuration) => {
  const dur = sourceDuration || 60;
  return {
    id: newId(),
    parent_asset_id: contentSource.parent_asset_id || contentSource.id,
    creator_id: contentSource.creator_id,
    tracks: DEFAULT_MASTER_TRACKS.map((t) => ({ ...t })),
    timeline_items: [{
      id: newId(), track_id: 'track-v1', item_type: 'video', content_source_id: contentSource.id,
      source_in_seconds: 0, source_out_seconds: dur, timeline_start_seconds: 0, duration_seconds: dur,
      transform: defaultTransform(), audio: defaultAudioProps(), text: null,
    }],
    width: 1920, height: 1080, frame_rate: 30, duration_seconds: dur, schema_version: 1,
  };
};

// Placeholder composition used until a real asset is loaded (or when the
// Editor is opened with no asset at all) so the full Main Editor layout —
// timeline, toolbar, preview, panels — always has a valid composition to
// render against instead of needing a null-guarded alternate screen.
const emptyMasterComposition = () => ({
  id: newId(),
  parent_asset_id: null,
  creator_id: null,
  tracks: DEFAULT_MASTER_TRACKS.map((t) => ({ ...t })),
  timeline_items: [],
  width: 1920, height: 1080, frame_rate: 30, duration_seconds: 60, schema_version: 1,
});

export default function UnifiedEditor() {
  const { contentSourceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Present whenever the Editor is opened from Production Hub's Editor tile
  // (which always navigates here with no asset id now — see
  // ProjectOverview.jsx). Used to scope the empty-state's Upload/Library/
  // pick-existing-video actions to the right project.
  const emptyStateProjectId = searchParams.get('projectId');
  const emptyStateReturnTo = searchParams.get('returnTo');
  const [emptyStateVideos, setEmptyStateVideos] = useState([]);
  const [emptyStateVideosLoading, setEmptyStateVideosLoading] = useState(false);
  const videoRef = useRef(null);
  const saveTimerRef = useRef(null);
  const pollTimersRef = useRef({});

  const [currentSourceId, setCurrentSourceId] = useState(contentSourceId);
  const [siblingSources, setSiblingSources] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [project, setProject] = useState(null);
  const projectRef = useRef(null);
  useEffect(() => { projectRef.current = project; }, [project]);
  const [contentSource, setContentSource] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamError, setStreamError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [activeOutputType, setActiveOutputType] = useState('lf');
  const [activeTab, setActiveTab] = useState('clips');
  const [selectedClipId, setSelectedClipId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);

  const [brandkits, setBrandkits] = useState([]);
  const [effectiveBrand, setEffectiveBrand] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiLastResult, setAiLastResult] = useState(null);
  const [gatewayUnavailable, setGatewayUnavailable] = useState(false);

  // ── Main Editor (Master composition) state — Stage 3A ──
  // `editorMode` is kept only as the internal flag the legacy Clip Editor
  // JSX below still reads from (trim/captions/brand/AI/review/queue for
  // generated clips) — it now opens in a dialog from the header instead of
  // competing with Main Editor as a visible mode switch (layout redesign).
  const [editorMode, setEditorMode] = useState('main'); // 'main' | 'clip'
  const [clipsDialogOpen, setClipsDialogOpen] = useState(false);
  const [masterComposition, setMasterComposition] = useState(() => emptyMasterComposition());
  const masterCompositionRef = useRef(null);
  useEffect(() => { masterCompositionRef.current = masterComposition; }, [masterComposition]);
  const [masterHistory, setMasterHistory] = useState([]);
  const [masterFuture, setMasterFuture] = useState([]);
  const [masterSelectedItemId, setMasterSelectedItemId] = useState(null);
  const [masterPlayhead, setMasterPlayhead] = useState(0);
  const [masterIsPlaying, setMasterIsPlaying] = useState(false);
  const [masterZoom, setMasterZoom] = useState(60);
  const [masterSnap, setMasterSnap] = useState(true);
  const [masterRipple, setMasterRipple] = useState(false);

  // ── Workspace layout (CapCut-style) — icon rail + expandable tool panel,
  // and a resizable/collapsible timeline. Level 1/2 UI state only. ──
  const [toolCategory, setToolCategory] = useState('media'); // media|audio|text|captions|effects|transitions|brand|ai|templates
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tracksHeight, setTracksHeight] = useState(260);
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);
  const timelineResizeRef = useRef(null);
  const startTimelineResize = (e) => {
    const startY = e.clientY;
    const startHeight = tracksHeight;
    timelineResizeRef.current = { startY, startHeight };
    const onMove = (ev) => {
      const delta = timelineResizeRef.current.startY - ev.clientY;
      setTracksHeight(Math.min(560, Math.max(140, timelineResizeRef.current.startHeight + delta)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const masterAutoFixPendingRef = useRef(true);
  const masterTimelineScrollRef = useRef(null);

  // ── Context-sensitive Properties (Stage: Main Editor final refinements) ──
  // Selecting a timeline item swaps the left tool panel into Properties;
  // the Back arrow restores whichever browsing category/state was open
  // before, without touching the current timeline selection.
  const [viewMode, setViewMode] = useState('category'); // 'category' | 'properties'
  const [lastCategoryBeforeProperties, setLastCategoryBeforeProperties] = useState('media');
  const toolCategoryRef = useRef(toolCategory);
  useEffect(() => { toolCategoryRef.current = toolCategory; }, [toolCategory]);
  useEffect(() => {
    if (masterSelectedItemId) {
      setViewMode((vm) => {
        if (vm !== 'properties') setLastCategoryBeforeProperties(toolCategoryRef.current);
        return 'properties';
      });
    } else {
      setViewMode('category');
    }
  }, [masterSelectedItemId]);
  const handleSelectCategory = (key) => { setViewMode('category'); setToolCategory(key); };
  const handleBackFromProperties = () => { setViewMode('category'); setToolCategory(lastCategoryBeforeProperties); };

  // ── Timeline markers (Stage: Main Editor final refinements) ──
  // Level 1/2 ONLY: stored in localStorage keyed by this master
  // composition's own id, NOT sent through the composition autosave —
  // `MasterComposition` on the backend uses `extra="ignore"`, so an
  // unknown `markers` field would be silently dropped on every save
  // rather than actually persisted. This still satisfies "available when
  // moving between Production Tools" within this browser/session; true
  // cross-device persistence needs the backend model extended first (see
  // completion report). Shares field names with Live's markers
  // (`label`, `elapsed_seconds`) so a later real integration doesn't need
  // a format translation — only `id` is Editor-specific (Live's markers
  // aren't individually addressable yet).
  const [markers, setMarkers] = useState([]);
  const markersStorageKey = masterComposition?.id ? `consistency_editor_markers_${masterComposition.id}` : null;
  useEffect(() => {
    if (!markersStorageKey) { setMarkers([]); return; }
    try { setMarkers(JSON.parse(localStorage.getItem(markersStorageKey) || '[]')); } catch { setMarkers([]); }
  }, [markersStorageKey]);
  const persistMarkers = (next) => {
    setMarkers(next);
    if (markersStorageKey) localStorage.setItem(markersStorageKey, JSON.stringify(next));
  };
  const addMarker = (atSeconds, label) => {
    const next = [...markers, { id: newId(), label: label || `Marker ${markers.length + 1}`, elapsed_seconds: Math.max(0, atSeconds) }]
      .sort((a, b) => a.elapsed_seconds - b.elapsed_seconds);
    persistMarkers(next);
  };
  const renameMarker = (id, label) => persistMarkers(markers.map((m) => (m.id === id ? { ...m, label } : m)));
  const moveMarker = (id, atSeconds) => persistMarkers(markers.map((m) => (m.id === id ? { ...m, elapsed_seconds: Math.max(0, atSeconds) } : m)).sort((a, b) => a.elapsed_seconds - b.elapsed_seconds));
  const removeMarker = (id) => persistMarkers(markers.filter((m) => m.id !== id));

  const applyEffectToSelected = (type) => {
    if (!masterSelectedItem || masterSelectedItem.item_type !== 'video') return;
    const effects = masterSelectedItem.effects || [];
    updateMasterItem(masterSelectedItemId, { effects: [...effects, { id: newId(), type, enabled: true, intensity_percent: 50, order: effects.length, keyframes: [] }] });
    toast.success('Effect applied — fine-tune it in Properties.');
  };
  const applyTransitionToSelected = (type) => {
    if (!masterSelectedItem || masterSelectedItem.item_type !== 'video') return;
    updateMasterItem(masterSelectedItemId, { transition_in: { type, duration_seconds: 0.5, direction: (type === 'wipe' || type === 'slide') ? 'left' : null } });
    toast.success('Transition applied — adjust duration in Properties.');
  };

  // Switching which video (of a multi-video Parent Asset) is being edited
  // loads a fresh, independent EditorProject for that source — never
  // fabricated, just the real GET for that id.
  const switchSource = (sourceId) => {
    if (sourceId === currentSourceId) return;
    Object.values(pollTimersRef.current).forEach(clearInterval);
    pollTimersRef.current = {};
    setSelectedClipId(null);
    setCurrentTime(0);
    setIsPlaying(false);
    setHistory([]);
    setFuture([]);
    setGatewayUnavailable(false);
    setLoading(true);
    setCurrentSourceId(sourceId);
    navigate(`/app/create/editor/${sourceId}`, { replace: true });
  };

  // ── Load project + source + brandkits ──
  useEffect(() => {
    if (!currentSourceId) {
      // Opened from Production Hub with no video yet — nothing to load;
      // the empty-state screen below handles Upload / Media Library entry.
      setLoading(false);
      return undefined;
    }
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/editor/projects/source/${currentSourceId}`, { credentials: 'include' });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Failed to load editor project (${res.status})`);
        }
        const data = await res.json();
        setProject(data.project);
        setContentSource(data.content_source);
        setSiblingSources(data.sibling_sources || []);
        setStreamUrl(data.stream_url);
        setStreamError(data.stream_error);

        if (data.project.master_composition) {
          setMasterComposition(data.project.master_composition);
          masterAutoFixPendingRef.current = false;
        } else {
          setMasterComposition(seedMasterComposition(data.content_source, data.project.source_duration));
          masterAutoFixPendingRef.current = !data.project.source_duration;
        }
        setMasterHistory([]);
        setMasterFuture([]);
        setMasterSelectedItemId(null);
        setMasterPlayhead(0);
        setMasterIsPlaying(false);

        const bkRes = await fetch(`${API_URL}/api/brandkits/creator/${data.content_source.creator_id}`, { credentials: 'include' });
        const creatorKits = bkRes.ok ? await bkRes.json() : [];
        let appKits = [];
        try {
          const appRes = await fetch(`${API_URL}/api/brandkits/app`, { credentials: 'include' });
          if (appRes.ok) appKits = await appRes.json();
        } catch { /* non-admin — app kits optional */ }
        setBrandkits([...creatorKits, ...appKits]);
      } catch (err) {
        setLoadError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { Object.values(pollTimersRef.current).forEach(clearInterval); };
  }, [currentSourceId]);

  // ── Empty-state: list this project's own video assets so the user can
  // pick one without leaving the Editor (reuses the same project overview
  // endpoint Production Hub itself uses — no new backend route) ──
  useEffect(() => {
    if (currentSourceId || !emptyStateProjectId) { setEmptyStateVideos([]); return; }
    setEmptyStateVideosLoading(true);
    const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'];
    const isImageAsset = (a) => IMAGE_EXTENSIONS.some((ext) => (a.input_file_url || '').toLowerCase().endsWith(ext));
    fetch(`${API_URL}/api/projects/${emptyStateProjectId}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { parent_assets: [] }))
      .then((data) => setEmptyStateVideos((data.parent_assets || []).filter((a) => !isImageAsset(a))))
      .catch(() => setEmptyStateVideos([]))
      .finally(() => setEmptyStateVideosLoading(false));
  }, [currentSourceId, emptyStateProjectId]);

  // ── Derived state ──
  const clipsOfActiveType = useMemo(
    () => (project?.clips || []).filter((c) => c.output_type === activeOutputType).sort((a, b) => a.order - b.order),
    [project, activeOutputType]
  );
  const allClipsSorted = useMemo(
    () => [...(project?.clips || [])].sort((a, b) => (a.output_type === b.output_type ? a.order - b.order : a.output_type.localeCompare(b.output_type))),
    [project]
  );
  const selectedClip = useMemo(() => (project?.clips || []).find((c) => c.id === selectedClipId) || null, [project, selectedClipId]);
  const selectedBrandkit = useMemo(() => brandkits.find((b) => b.id === selectedClip?.brandkit_id), [brandkits, selectedClip]);
  const effectiveDuration = useMemo(() => {
    if (project?.source_duration) return project.source_duration;
    const maxOut = Math.max(0, ...(project?.clips || []).map((c) => c.out_point));
    return Math.max(60, maxOut);
  }, [project]);
  const activeCaption = useMemo(() => {
    if (!selectedClip) return null;
    const local = currentTime - selectedClip.in_point;
    return selectedClip.captions.find((c) => local >= c.start && local <= c.end) || null;
  }, [selectedClip, currentTime]);

  // ── Autosave (debounced) ──
  const pendingSaveRef = useRef(null);
  const inFlightSaveRef = useRef(null);

  const flushAutosave = useCallback(async () => {
    clearTimeout(saveTimerRef.current);
    const pending = pendingSaveRef.current;
    pendingSaveRef.current = null;

    if (pending && project) {
      setSaving(true);
      const savePromise = (async () => {
        let res;
        try {
          res = await fetch(`${API_URL}/api/editor/projects/${project.id}`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(pending),
          });
        } catch (networkErr) {
          setSaving(false);
          if (inFlightSaveRef.current === savePromise) inFlightSaveRef.current = null;
          // Put the failed edit back so it isn't silently lost — the next
          // flushAutosave() (a manual retry, the next debounce tick, or a
          // Production Tools navigation attempt) will resend it instead of
          // finding nothing pending and sailing through.
          pendingSaveRef.current = { ...pending, ...(pendingSaveRef.current || {}) };
          toast.error('Failed to save edit — check your connection');
          throw networkErr;
        }
        setSaving(false);
        if (inFlightSaveRef.current === savePromise) inFlightSaveRef.current = null;
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const message = err.detail?.message || err.detail || 'Failed to save edit';
          pendingSaveRef.current = { ...pending, ...(pendingSaveRef.current || {}) };
          toast.error(message);
          throw new Error(message);
        }
      })();
      inFlightSaveRef.current = savePromise;
    }

    // Always wait for whatever is in-flight — even a save started by a
    // different flushAutosave()/timer call — so a caller like submitRender
    // is strictly ordered after the latest write, not just the latest
    // UNSENT edit.
    if (inFlightSaveRef.current) {
      await inFlightSaveRef.current;
    }
  }, [project]);

  const scheduleAutosave = useCallback((clips, extra = {}) => {
    if (!project) return;
    pendingSaveRef.current = { ...(pendingSaveRef.current || {}), clips, ...extra };
    setSaving(true);
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => { flushAutosave().catch(() => {}); }, 900);
  }, [project, flushAutosave]);

  // applyClips/undo/redo are intentionally flat: every setState call below
  // reads only from the outer closure (never from another setState's
  // updater) and no setState is ever invoked from inside another's
  // updater function. React 18 StrictMode double-invokes updater
  // functions as a purity check — nested setState calls would then fire
  // twice for real, which is exactly what corrupted undo/redo before.
  const applyClips = useCallback((nextClips, { skipHistory = false } = {}) => {
    if (!skipHistory) {
      setHistory((h) => [...h.slice(-49), project.clips]);
      setFuture([]);
    }
    setProject((p) => ({ ...p, clips: nextClips }));
    scheduleAutosave(nextClips);
  }, [project, scheduleAutosave]);

  // ── Undo / Redo ──
  const undo = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    setFuture((f) => [...f, project.clips]);
    setProject((p) => ({ ...p, clips: prev }));
    scheduleAutosave(prev);
  }, [history, project, scheduleAutosave]);

  const redo = useCallback(() => {
    if (future.length === 0) return;
    const next = future[future.length - 1];
    setFuture((f) => f.slice(0, -1));
    setHistory((h) => [...h, project.clips]);
    setProject((p) => ({ ...p, clips: next }));
    scheduleAutosave(next);
  }, [future, project, scheduleAutosave]);

  // ── Master composition mutations (Main Editor) ──
  const applyMaster = useCallback((nextMaster, { skipHistory = false } = {}) => {
    masterAutoFixPendingRef.current = false;
    if (!skipHistory) {
      setMasterHistory((h) => [...h.slice(-49), masterCompositionRef.current]);
      setMasterFuture([]);
    }
    setMasterComposition(nextMaster);
    scheduleAutosave(project?.clips, { master_composition: nextMaster });
  }, [project, scheduleAutosave]);

  const undoMaster = useCallback(() => {
    if (masterHistory.length === 0) return;
    const prev = masterHistory[masterHistory.length - 1];
    setMasterHistory((h) => h.slice(0, -1));
    setMasterFuture((f) => [...f, masterCompositionRef.current]);
    setMasterComposition(prev);
    scheduleAutosave(project?.clips, { master_composition: prev });
  }, [masterHistory, project, scheduleAutosave]);

  const redoMaster = useCallback(() => {
    if (masterFuture.length === 0) return;
    const next = masterFuture[masterFuture.length - 1];
    setMasterFuture((f) => f.slice(0, -1));
    setMasterHistory((h) => [...h, masterCompositionRef.current]);
    setMasterComposition(next);
    scheduleAutosave(project?.clips, { master_composition: next });
  }, [masterFuture, project, scheduleAutosave]);

  // ── Track management (Main Editor layout redesign) — compatible with the
  // existing MasterTrack schema (open-ended list, no backend change needed).
  // Added tracks are flagged `custom: true` so only empty, user-added
  // tracks can ever be removed; the six defaults are permanent. ──
  const addTrack = (type) => {
    const sameType = masterComposition.tracks.filter((t) => t.type === type);
    const nextOrder = Math.max(0, ...masterComposition.tracks.map((t) => t.order)) + 1;
    const prefix = type === 'video' ? 'V' : type === 'audio' ? 'A' : 'T';
    const newTrack = {
      id: newId(), type, order: nextOrder, name: `${prefix}${sameType.length + 1}`,
      locked: false, muted: false, hidden: false, custom: true,
    };
    applyMaster({ ...masterComposition, tracks: [...masterComposition.tracks, newTrack] });
    toast.success(`${newTrack.name} track added`);
  };

  const removeTrack = (trackId) => {
    const track = masterComposition.tracks.find((t) => t.id === trackId);
    if (!track || !track.custom) return;
    const hasItems = masterComposition.timeline_items.some((i) => i.track_id === trackId);
    if (hasItems) { toast.info('Remove its clips first — tracks with media on them can’t be removed.'); return; }
    if (!window.confirm(`Remove empty track "${track.name}"? This only removes the track — no media is deleted.`)) return;
    applyMaster({ ...masterComposition, tracks: masterComposition.tracks.filter((t) => t.id !== trackId) });
  };

  const recalcMasterDuration = (items, prevDuration = 0) =>
    Math.max(prevDuration, 0, ...items.map((i) => i.timeline_start_seconds + i.duration_seconds));

  const handleMasterDurationKnown = (d) => {
    if (!masterAutoFixPendingRef.current || !d || !masterComposition) return;
    masterAutoFixPendingRef.current = false;
    if (masterComposition.timeline_items.length !== 1) return;
    const item = masterComposition.timeline_items[0];
    if (item.timeline_start_seconds !== 0) return;
    const fixed = { ...masterComposition, duration_seconds: d, timeline_items: [{ ...item, source_out_seconds: d, duration_seconds: d }] };
    applyMaster(fixed, { skipHistory: true });
    if (!project.source_duration) {
      setProject((p) => ({ ...p, source_duration: d }));
    }
  };

  const addSourceToTimeline = (source) => {
    if (!masterComposition) return;
    const videoTracks = masterComposition.tracks.filter((t) => t.type === 'video').sort((a, b) => a.order - b.order);
    const targetTrack = videoTracks[0];
    if (!targetTrack) return;
    const itemsOnTrack = masterComposition.timeline_items.filter((i) => i.track_id === targetTrack.id);
    const endOfTrack = Math.max(0, ...itemsOnTrack.map((i) => i.timeline_start_seconds + i.duration_seconds));
    const dur = source.duration_seconds || 10;
    const newItem = {
      id: newId(), track_id: targetTrack.id, item_type: 'video', content_source_id: source.id,
      source_in_seconds: 0, source_out_seconds: dur, timeline_start_seconds: endOfTrack, duration_seconds: dur,
      transform: defaultTransform(), audio: defaultAudioProps(), text: null,
    };
    const items = [...masterComposition.timeline_items, newItem];
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
    setMasterSelectedItemId(newItem.id);
  };

  const dropSourceOnTimeline = (trackId, contentSourceId, startSeconds) => {
    const source = siblingSources.find((s) => s.id === contentSourceId);
    const track = masterComposition?.tracks.find((t) => t.id === trackId);
    if (!source || !track || track.locked || (track.type !== 'video' && track.type !== 'audio')) return;
    const dur = source.duration_seconds || 10;
    const newItem = {
      id: newId(), track_id: trackId, item_type: track.type, content_source_id: contentSourceId,
      source_in_seconds: 0, source_out_seconds: dur, timeline_start_seconds: Math.max(0, startSeconds), duration_seconds: dur,
      transform: track.type === 'video' ? defaultTransform() : null, audio: defaultAudioProps(), text: null,
    };
    const items = [...masterComposition.timeline_items, newItem];
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
    setMasterSelectedItemId(newItem.id);
  };

  const addTextItem = (trackId, atSeconds) => {
    const newItem = {
      id: newId(), track_id: trackId, item_type: 'text', content_source_id: null,
      source_in_seconds: 0, source_out_seconds: 3, timeline_start_seconds: Math.max(0, atSeconds), duration_seconds: 3,
      transform: null, audio: null,
      text: {
        text: 'New Text', font_family: effectiveBrandKit?.font_family || 'Inter', font_size: 32,
        color: effectiveBrandKit?.primary_color || '#FFFFFF', brand_inherited: !!effectiveBrandKit,
      },
    };
    const items = [...masterComposition.timeline_items, newItem];
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
    setMasterSelectedItemId(newItem.id);
  };

  // ── Stage 3C: captions, duplicate, text style presets, safe zones ──
  const addCaptionBlock = (trackId, atSeconds) => {
    if (!trackId) return;
    const newItem = {
      id: newId(), track_id: trackId, item_type: 'text', content_source_id: null,
      source_in_seconds: 0, source_out_seconds: 5, timeline_start_seconds: Math.max(0, atSeconds), duration_seconds: 5,
      transform: null, audio: null,
      text: {
        text: '', font_family: effectiveBrandKit?.font_family || 'Inter', font_size: 28,
        color: effectiveBrandKit?.primary_color || '#FFFFFF', brand_inherited: !!effectiveBrandKit,
        segments: [{ id: newId(), start_seconds: 0, end_seconds: 2, text: 'New caption', words: [] }],
      },
    };
    const items = [...masterComposition.timeline_items, newItem];
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
    setMasterSelectedItemId(newItem.id);
  };

  const duplicateMasterItem = (itemId) => {
    const item = masterComposition.timeline_items.find((i) => i.id === itemId);
    if (!item) return;
    const dup = { ...item, id: newId(), timeline_start_seconds: item.timeline_start_seconds + item.duration_seconds };
    const items = [...masterComposition.timeline_items, dup];
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
    setMasterSelectedItemId(dup.id);
    toast.success('Item duplicated');
  };

  const saveTextStylePreset = (name) => {
    const item = masterSelectedItem;
    if (!item || !name) return;
    const t = item.text || {};
    const preset = {
      id: newId(), name,
      font_family: t.font_family || 'Inter', font_size: t.font_size || 32, font_weight: t.font_weight || 'normal',
      color: t.color || '#FFFFFF', alignment: t.alignment || 'center', outline_color: t.outline_color || null,
      outline_width: t.outline_width || 0, shadow_enabled: !!t.shadow_enabled, shadow_color: t.shadow_color || '#000000',
      background_color: t.background_color || null, background_opacity: t.background_opacity || 0,
    };
    const presets = [...(masterComposition.text_style_presets || []), preset];
    applyMaster({ ...masterComposition, text_style_presets: presets });
    toast.success(`Saved style "${name}"`);
  };

  const toggleSafeZones = () => applyMaster({ ...masterComposition, show_safe_zones: !masterComposition.show_safe_zones }, { skipHistory: true });

  // ── Stage 3D: Brand Kit resolution + defaults for the Main Editor ──
  const fetchEffectiveBrand = useCallback(async () => {
    if (!project?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/brand/effective`, { credentials: 'include' });
      if (res.ok) setEffectiveBrand(await res.json());
    } catch {
      // Non-critical — brand defaults simply won't auto-apply until the next load.
    }
  }, [project?.id]);

  useEffect(() => { fetchEffectiveBrand(); }, [fetchEffectiveBrand]);

  const effectiveBrandKit = effectiveBrand?.brand_kit || null;

  // ── Stage 3E: Master approval + versioning state ──
  const [activeApprovalSnapshot, setActiveApprovalSnapshot] = useState(null);
  const [masterVersions, setMasterVersions] = useState([]);
  const [approvingMaster, setApprovingMaster] = useState(false);
  const [generatingClips, setGeneratingClips] = useState(false);
  const [clipRangeDraft, setClipRangeDraft] = useState({ start: 0, end: 0 });

  const fetchMasterApprovalState = useCallback(async () => {
    if (!project?.id) return;
    try {
      const [apprRes, verRes] = await Promise.all([
        fetch(`${API_URL}/api/editor/projects/${project.id}/master/approved`, { credentials: 'include' }),
        fetch(`${API_URL}/api/editor/projects/${project.id}/master/versions`, { credentials: 'include' }),
      ]);
      if (apprRes.ok) setActiveApprovalSnapshot((await apprRes.json()).snapshot || null);
      if (verRes.ok) setMasterVersions((await verRes.json()).versions || []);
    } catch {
      // Non-critical — the Publish tab simply shows stale data until the next load/action.
    }
  }, [project?.id]);

  useEffect(() => { fetchMasterApprovalState(); }, [fetchMasterApprovalState]);

  const approveMaster = async () => {
    if (!project) return;
    await flushAutosave();
    setApprovingMaster(true);
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/master/approve`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail?.message || data.detail || 'Could not approve Master'); return; }
      setMasterComposition(data.master_composition);
      setActiveApprovalSnapshot(data.snapshot);
      fetchMasterApprovalState();
      toast.success(`Master approved — version ${data.snapshot.version}`);
    } catch {
      toast.error('Network error approving Master');
    } finally {
      setApprovingMaster(false);
    }
  };

  const revokeMasterApproval = async () => {
    if (!project) return;
    setApprovingMaster(true);
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/master/revoke`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail?.message || data.detail || 'Could not revoke approval'); return; }
      setMasterComposition(data.master_composition);
      setActiveApprovalSnapshot(null);
      toast.info('Approval revoked — the Master is a draft again');
    } catch {
      toast.error('Network error revoking approval');
    } finally {
      setApprovingMaster(false);
    }
  };

  const generateClips = async (payload) => {
    if (!project) return;
    setGeneratingClips(true);
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/generate`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail?.message || data.detail || 'Could not generate clips'); return; }
      setProject((p) => ({ ...p, clips: [...p.clips, ...data.clips] }));
      toast.success(`Generated ${data.clips.length} clip${data.clips.length !== 1 ? 's' : ''}`);
      setActiveOutputType(payload.output_type);
      setEditorMode('clip');
      setClipsDialogOpen(true);
      setActiveTab('queue');
    } catch {
      toast.error('Network error generating clips');
    } finally {
      setGeneratingClips(false);
    }
  };

  const generateClipsFromSuggestions = async (payload) => {
    if (!project) return;
    setGeneratingClips(true);
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/generate-from-suggestions`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail?.message || data.detail || 'Could not generate clips'); return; }
      if (data.clips?.length) {
        setProject((p) => ({ ...p, clips: [...p.clips, ...data.clips] }));
        toast.success(`Generated ${data.clips.length} clip${data.clips.length !== 1 ? 's' : ''} from AI suggestions`);
        setEditorMode('clip');
        setClipsDialogOpen(true);
        setActiveTab('queue');
      }
      if (data.skipped?.length) toast.info(`${data.skipped.length} suggestion(s) skipped — ${data.skipped[0].reason}`);
    } catch {
      toast.error('Network error generating clips from suggestions');
    } finally {
      setGeneratingClips(false);
    }
  };

  const batchApproveClips = async (clipIds, approved) => {
    if (!project) return;
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/batch-approve`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clip_ids: clipIds, approved }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail?.message || data.detail || 'Could not update approval'); return; }
      const updatedById = Object.fromEntries(data.clips.map((c) => [c.id, c]));
      setProject((p) => ({ ...p, clips: p.clips.map((c) => updatedById[c.id] || c) }));
      toast.success(`${approved ? 'Approved' : 'Un-approved'} ${data.clips.length} clip${data.clips.length !== 1 ? 's' : ''}`);
    } catch {
      toast.error('Network error updating approval');
    }
  };

  const retrimMasterClip = async (clipId, startSeconds, endSeconds, targetAspectRatio) => {
    if (!project) return;
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/${clipId}/retrim`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_seconds: startSeconds, end_seconds: endSeconds, target_aspect_ratio: targetAspectRatio ?? null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail?.message || data.detail || 'Could not update this clip\'s range'); return; }
      setProject((p) => ({ ...p, clips: p.clips.map((c) => (c.id === clipId ? data.clip : c)) }));
      toast.success('Clip range updated');
    } catch {
      toast.error('Network error updating clip range');
    }
  };

  useEffect(() => {
    if (selectedClip?.master_composition_slice) {
      setClipRangeDraft({ start: selectedClip.master_range_start_seconds ?? 0, end: selectedClip.master_range_end_seconds ?? 0 });
    }
  }, [selectedClipId]);

  const updateBranding = (partial) => applyMaster({ ...masterComposition, branding: { ...(masterComposition.branding || {}), ...partial } }, { skipHistory: true });

  const setBrandKitOverride = (id) => { updateBranding({ brand_kit_id: id }); setTimeout(fetchEffectiveBrand, 300); };

  const toggleWatermark = (enabled, logoUrl) => updateBranding({
    watermark: { ...(masterComposition.branding?.watermark || { position: 'bottom_right', width_percent: 12, opacity_percent: 85 }), enabled, logo_url: logoUrl || masterComposition.branding?.watermark?.logo_url || null },
  });
  const updateWatermark = (partial) => updateBranding({ watermark: { ...(masterComposition.branding?.watermark || {}), ...partial } });

  const insertBrandIntro = (sourceId) => {
    const source = siblingSources.find((s) => s.id === sourceId);
    if (!source) return;
    const dur = source.duration_seconds || 3;
    const shifted = masterComposition.timeline_items.map((i) => ({ ...i, timeline_start_seconds: i.timeline_start_seconds + dur }));
    const introItem = {
      id: newId(), track_id: 'track-v1', item_type: 'video', content_source_id: sourceId,
      source_in_seconds: 0, source_out_seconds: dur, timeline_start_seconds: 0, duration_seconds: dur,
      transform: defaultTransform(), audio: defaultAudioProps(), text: null,
    };
    const items = [introItem, ...shifted];
    applyMaster({
      ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds + dur),
      branding: { ...(masterComposition.branding || {}), intro_source_id: sourceId },
    });
    toast.success('Intro inserted — every existing clip shifted later');
  };

  const insertBrandOutro = (sourceId) => {
    const source = siblingSources.find((s) => s.id === sourceId);
    if (!source) return;
    const dur = source.duration_seconds || 3;
    const outroItem = {
      id: newId(), track_id: 'track-v1', item_type: 'video', content_source_id: sourceId,
      source_in_seconds: 0, source_out_seconds: dur, timeline_start_seconds: masterComposition.duration_seconds, duration_seconds: dur,
      transform: defaultTransform(), audio: defaultAudioProps(), text: null,
    };
    const items = [...masterComposition.timeline_items, outroItem];
    applyMaster({
      ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds + dur),
      branding: { ...(masterComposition.branding || {}), outro_source_id: sourceId },
    });
    toast.success('Outro appended after the last clip');
  };

  const generateBrandPresets = () => {
    if (!effectiveBrandKit) return;
    const presets = [
      {
        id: newId(), name: 'Brand Title', font_family: effectiveBrandKit.font_family, font_size: 48, font_weight: 'bold',
        color: effectiveBrandKit.primary_color, alignment: 'center', outline_width: 0, shadow_enabled: true,
        shadow_color: '#000000', background_color: null, background_opacity: 0, source: 'brand_kit',
      },
      {
        id: newId(), name: 'Brand Lower Third', font_family: effectiveBrandKit.font_family, font_size: 28, font_weight: 'normal',
        color: '#FFFFFF', alignment: 'left', outline_width: 0, shadow_enabled: false, shadow_color: '#000000',
        background_color: effectiveBrandKit.secondary_color, background_opacity: 80, source: 'brand_kit',
      },
    ];
    const existingCustom = (masterComposition.text_style_presets || []).filter((p) => p.source !== 'brand_kit');
    applyMaster({ ...masterComposition, text_style_presets: [...existingCustom, ...presets] }, { skipHistory: true });
    toast.success('Brand presets added — pick them from the Text tab\'s style preset list');
  };

  // ── Stage 3D: AI Tools orchestration (Main Editor) ──
  const requestTranscription = async (contentSourceId) => {
    if (!project) { toast.info('Upload or select a video first'); throw new Error('No project yet'); }
    const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/transcription/request`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content_source_id: contentSourceId }),
    });
    if (!res.ok) throw new Error('Transcription request failed');
    return res.json();
  };

  const requestAiAnalysis = async (capability, contentSourceId, params = {}) => {
    if (!project) { toast.info('Upload or select a video first'); return; }
    const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/ai-analysis/request`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content_source_id: contentSourceId, capability, params }),
    });
    if (!res.ok) throw new Error('AI analysis request failed');
    const job = await res.json();
    if (job.status === 'completed') {
      // Never executes today — no capability can reach "completed"
      // without a connected provider — but refreshes any newly-derived
      // suggestions the backend appended to the project once one can.
      try {
        const projRes = await fetch(`${API_URL}/api/editor/projects/${project.id}`, { credentials: 'include' });
        if (projRes.ok) {
          const fresh = await projRes.json();
          setProject((p) => ({ ...p, ai_edit_suggestions: fresh.ai_edit_suggestions || [] }));
        }
      } catch { /* non-critical */ }
    }
    return job;
  };

  const applyCutRangeSuggestion = (suggestion) => {
    if (!masterSelectedItem || masterSelectedItem.content_source_id !== suggestion.content_source_id) {
      toast.error('Select the matching clip on the Master timeline first, then accept this suggestion');
      return;
    }
    const item = masterSelectedItem;
    const cutStart = Math.max(item.source_in_seconds, suggestion.start_seconds);
    const cutEnd = Math.min(item.source_out_seconds, suggestion.end_seconds);
    if (cutEnd <= cutStart) return;
    const speed = (item.speed_percent ?? 100) / 100 || 1;
    const beforeDur = (cutStart - item.source_in_seconds) / speed;
    const afterDur = (item.source_out_seconds - cutEnd) / speed;
    const removedDur = item.duration_seconds - beforeDur - afterDur;
    const newItems = [];
    if (beforeDur > 0.05) newItems.push({ ...item, id: newId(), source_out_seconds: cutStart, duration_seconds: beforeDur });
    if (afterDur > 0.05) newItems.push({ ...item, id: newId(), source_in_seconds: cutEnd, timeline_start_seconds: item.timeline_start_seconds + beforeDur, duration_seconds: afterDur });
    const rest = masterComposition.timeline_items
      .filter((i) => i.id !== item.id)
      .map((i) => (i.track_id === item.track_id && i.timeline_start_seconds > item.timeline_start_seconds ? { ...i, timeline_start_seconds: i.timeline_start_seconds - removedDur } : i));
    const items = [...rest, ...newItems];
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
    setMasterSelectedItemId(newItems[0]?.id || null);
    toast.success('Cut applied to the Master timeline');
  };

  const setAiEditSuggestionStatus = async (suggestionId, status) => {
    const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/ai-edits/${suggestionId}/${status === 'accepted' ? 'accept' : 'reject'}`, {
      method: 'POST', credentials: 'include',
    });
    if (!res.ok) return;
    const updated = await res.json();
    const next = (project.ai_edit_suggestions || []).map((s) => (s.id === suggestionId ? updated : s));
    setProject((p) => ({ ...p, ai_edit_suggestions: next }));
    scheduleAutosave(project.clips, { ai_edit_suggestions: next });
    if (status === 'accepted' && updated.kind === 'cut_range') applyCutRangeSuggestion(updated);
  };
  const acceptAiSuggestion = (id) => setAiEditSuggestionStatus(id, 'accepted');
  const rejectAiSuggestion = (id) => setAiEditSuggestionStatus(id, 'rejected');

  const moveMasterItem = useCallback((itemId, newTrackId, newStartSeconds) => {
    const mc = masterCompositionRef.current;
    const item = mc?.timeline_items.find((i) => i.id === itemId);
    const newTrack = mc?.tracks.find((t) => t.id === newTrackId);
    if (!item || !newTrack || newTrack.locked || newTrack.type !== item.item_type) return;
    const items = mc.timeline_items.map((i) => (i.id === itemId ? { ...i, track_id: newTrackId, timeline_start_seconds: Math.max(0, newStartSeconds) } : i));
    applyMaster({ ...mc, timeline_items: items, duration_seconds: recalcMasterDuration(items) });
  }, [applyMaster]);

  const trimMasterItem = useCallback((itemId, edge, deltaSeconds) => {
    const mc = masterCompositionRef.current;
    if (!mc) return;
    const items = mc.timeline_items.map((i) => {
      if (i.id !== itemId) return i;
      const sourceMeta = siblingSources.find((s) => s.id === i.content_source_id);
      if (edge === 'start') {
        const maxDelta = i.duration_seconds - MIN_MASTER_ITEM_DURATION;
        const minDelta = -i.source_in_seconds;
        const d = Math.max(minDelta, Math.min(maxDelta, deltaSeconds));
        return { ...i, timeline_start_seconds: Math.max(0, i.timeline_start_seconds + d), source_in_seconds: i.source_in_seconds + d, duration_seconds: i.duration_seconds - d };
      }
      const minDelta = -(i.duration_seconds - MIN_MASTER_ITEM_DURATION);
      const maxDelta = sourceMeta?.duration_seconds ? sourceMeta.duration_seconds - i.source_out_seconds : Infinity;
      const d = Math.max(minDelta, Math.min(maxDelta, deltaSeconds));
      return { ...i, duration_seconds: i.duration_seconds + d, source_out_seconds: i.source_out_seconds + d };
    });
    applyMaster({ ...mc, timeline_items: items, duration_seconds: recalcMasterDuration(items) });
  }, [applyMaster, siblingSources]);

  const updateMasterItem = (itemId, partial) => {
    const items = masterComposition.timeline_items.map((i) => (i.id === itemId ? { ...i, ...partial } : i));
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
  };

  const toggleTrackFlag = (trackId, flag) => {
    const tracks = masterComposition.tracks.map((t) => (t.id === trackId ? { ...t, [flag]: !t[flag] } : t));
    applyMaster({ ...masterComposition, tracks });
  };

  const masterSelectedItem = useMemo(
    () => masterComposition?.timeline_items.find((i) => i.id === masterSelectedItemId) || null,
    [masterComposition, masterSelectedItemId]
  );
  const masterSelectedItemSource = useMemo(
    () => siblingSources.find((s) => s.id === masterSelectedItem?.content_source_id) || null,
    [siblingSources, masterSelectedItem]
  );

  const splitMasterItem = () => {
    const item = masterSelectedItem;
    if (!item) return;
    const localOffset = masterPlayhead - item.timeline_start_seconds;
    if (localOffset <= 0.1 || localOffset >= item.duration_seconds - 0.1) {
      toast.error('Move the playhead inside the clip to split it');
      return;
    }
    const first = { ...item, duration_seconds: localOffset, source_out_seconds: item.source_in_seconds + localOffset };
    const second = { ...item, id: newId(), timeline_start_seconds: item.timeline_start_seconds + localOffset, source_in_seconds: item.source_in_seconds + localOffset, duration_seconds: item.duration_seconds - localOffset };
    const items = masterComposition.timeline_items.map((i) => (i.id === item.id ? first : i)).concat(second);
    applyMaster({ ...masterComposition, timeline_items: items });
    setMasterSelectedItemId(second.id);
    toast.success('Clip split at playhead');
  };

  const deleteMasterItem = () => {
    const item = masterSelectedItem;
    if (!item) return;
    let items = masterComposition.timeline_items.filter((i) => i.id !== item.id);
    if (masterRipple) {
      items = items.map((i) => (i.track_id === item.track_id && i.timeline_start_seconds > item.timeline_start_seconds)
        ? { ...i, timeline_start_seconds: Math.max(0, i.timeline_start_seconds - item.duration_seconds) } : i);
    }
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items) });
    setMasterSelectedItemId(null);
  };

  const fitMasterZoom = () => {
    const widthPx = masterTimelineScrollRef.current?.clientWidth || 800;
    const dur = Math.max(1, masterComposition?.duration_seconds || 1);
    setMasterZoom(Math.max(10, Math.min(240, Math.floor((widthPx - 150) / dur))));
  };

  // ── Stage 3B: crop / speed / PiP / reframing / audio / ducking / merge / aspect ratio ──
  const masterSelectedItemTrack = useMemo(
    () => masterComposition?.tracks.find((t) => t.id === masterSelectedItem?.track_id) || null,
    [masterComposition, masterSelectedItem]
  );

  const setItemSpeed = (itemId, speedPercent) => {
    const item = masterComposition.timeline_items.find((i) => i.id === itemId);
    if (!item) return;
    const sourceSpan = Math.max(0.1, item.source_out_seconds - item.source_in_seconds);
    const newDuration = sourceSpan / (speedPercent / 100);
    const items = masterComposition.timeline_items.map((i) => (i.id === itemId ? { ...i, speed_percent: speedPercent, duration_seconds: newDuration } : i));
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
  };

  const togglePip = (itemId) => {
    const item = masterComposition.timeline_items.find((i) => i.id === itemId);
    if (!item) return;
    const next = !item.is_pip;
    const transform = next
      ? { ...defaultTransform(), scale_percent: 35, position_x: Math.round(masterComposition.width * 0.28), position_y: Math.round(masterComposition.height * 0.28) }
      : defaultTransform();
    updateMasterItem(itemId, { is_pip: next, transform });
  };

  const setReframing = (itemId, enabled, targetAspectRatio) => {
    const reframing = enabled
      ? { enabled: true, target_aspect_ratio: targetAspectRatio || '9:16', status: 'pending_analysis', tracked_crop_keyframes: [] }
      : { enabled: false, target_aspect_ratio: targetAspectRatio || '9:16', status: 'not_requested', tracked_crop_keyframes: [] };
    updateMasterItem(itemId, { reframing });
  };

  const addKeyframeAtPlayhead = (itemId) => {
    const item = masterComposition.timeline_items.find((i) => i.id === itemId);
    if (!item) return;
    const localTime = Math.max(0, Math.min(item.duration_seconds, masterPlayhead - item.timeline_start_seconds));
    const base = item.transform || defaultTransform();
    const kf = { id: newId(), time_seconds: localTime, ...base };
    const keyframes = [...(item.keyframes || []), kf].sort((a, b) => a.time_seconds - b.time_seconds);
    updateMasterItem(itemId, { keyframes });
  };

  const removeKeyframe = (itemId, keyframeId) => {
    const item = masterComposition.timeline_items.find((i) => i.id === itemId);
    if (!item) return;
    updateMasterItem(itemId, { keyframes: (item.keyframes || []).filter((k) => k.id !== keyframeId) });
  };

  const detachAudio = (itemId) => {
    const item = masterComposition.timeline_items.find((i) => i.id === itemId);
    if (!item || item.item_type !== 'video') return;
    const audioTracks = masterComposition.tracks.filter((t) => t.type === 'audio').sort((a, b) => a.order - b.order);
    const targetTrack = audioTracks.find((t) => !masterComposition.timeline_items.some((i) =>
      i.track_id === t.id && i.timeline_start_seconds < item.timeline_start_seconds + item.duration_seconds && i.timeline_start_seconds + i.duration_seconds > item.timeline_start_seconds
    )) || audioTracks[0];
    if (!targetTrack) { toast.error('No audio track available'); return; }
    const audioItem = {
      id: newId(), track_id: targetTrack.id, item_type: 'audio', content_source_id: item.content_source_id,
      source_in_seconds: item.source_in_seconds, source_out_seconds: item.source_out_seconds,
      timeline_start_seconds: item.timeline_start_seconds, duration_seconds: item.duration_seconds,
      transform: null, audio: item.audio || defaultAudioProps(), text: null, speed_percent: item.speed_percent ?? 100,
      detached_from_item_id: item.id,
    };
    const items = masterComposition.timeline_items.map((i) => (i.id === itemId ? { ...i, audio_detached: true } : i)).concat(audioItem);
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
    setMasterSelectedItemId(audioItem.id);
    toast.success(`Audio detached onto ${targetTrack.name || 'an audio track'}`);
  };

  const replaceAudioSource = (itemId, newSourceId) => {
    const newSource = siblingSources.find((s) => s.id === newSourceId);
    if (!newSource) return;
    const items = masterComposition.timeline_items.map((i) => {
      if (i.id !== itemId) return i;
      const dur = Math.min(i.duration_seconds, newSource.duration_seconds || i.duration_seconds);
      return { ...i, content_source_id: newSourceId, source_in_seconds: 0, source_out_seconds: dur, duration_seconds: dur, detached_from_item_id: null };
    });
    applyMaster({ ...masterComposition, timeline_items: items, duration_seconds: recalcMasterDuration(items, masterComposition.duration_seconds) });
  };

  const setAudioProcessing = (itemId, partial) => {
    const item = masterComposition.timeline_items.find((i) => i.id === itemId);
    if (!item) return;
    const existing = item.audio?.processing || { noise_reduction_enabled: false, noise_reduction_strength_percent: 50, voice_enhancement_enabled: false, status: 'not_requested', processed_b2_key: null, error: null };
    const next = { ...existing, ...partial };
    // No authorized audio-processing service exists yet — enabling either
    // toggle is honestly marked pending, never "completed".
    next.status = (next.noise_reduction_enabled || next.voice_enhancement_enabled) ? 'pending_integration' : 'not_requested';
    updateMasterItem(itemId, { audio: { ...(item.audio || defaultAudioProps()), processing: next } });
  };

  const updateTrackDucking = (trackId, partial) => {
    if (!trackId) return;
    const tracks = masterComposition.tracks.map((t) => (t.id === trackId
      ? { ...t, ducking: { ...(t.ducking || { enabled: false, trigger_track_id: null, duck_level_percent: 30, attack_ms: 150, release_ms: 400 }), ...partial } }
      : t));
    applyMaster({ ...masterComposition, tracks });
  };

  const cacheWaveform = (itemId, waveform) => {
    const item = masterComposition.timeline_items.find((i) => i.id === itemId);
    if (!item) return;
    updateMasterItem(itemId, { audio: { ...(item.audio || defaultAudioProps()), waveform } });
  };

  const mergeWithNextItem = () => {
    const item = masterSelectedItem;
    if (!item) return;
    const neighbor = masterComposition.timeline_items.find((i) =>
      i.track_id === item.track_id && i.id !== item.id &&
      Math.abs(i.timeline_start_seconds - (item.timeline_start_seconds + item.duration_seconds)) < 0.05 &&
      i.content_source_id === item.content_source_id &&
      Math.abs(i.source_in_seconds - item.source_out_seconds) < 0.05
    );
    if (!neighbor) { toast.error("No adjacent, contiguous clip to merge with"); return; }
    const merged = { ...item, source_out_seconds: neighbor.source_out_seconds, duration_seconds: item.duration_seconds + neighbor.duration_seconds };
    const items = masterComposition.timeline_items.filter((i) => i.id !== neighbor.id).map((i) => (i.id === item.id ? merged : i));
    applyMaster({ ...masterComposition, timeline_items: items });
    toast.success('Clips merged');
  };

  const ASPECT_RATIO_DIMENSIONS = { '16:9': [1920, 1080], '9:16': [1080, 1920], '1:1': [1080, 1080], '4:5': [1080, 1350] };
  const setAspectRatio = (ratio) => {
    const [w, h] = ASPECT_RATIO_DIMENSIONS[ratio] || ASPECT_RATIO_DIMENSIONS['16:9'];
    applyMaster({ ...masterComposition, aspect_ratio: ratio, width: w, height: h });
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (!clipsDialogOpen) {
          e.shiftKey ? redoMaster() : undoMaster();
        } else {
          e.shiftKey ? redo() : undo();
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, undoMaster, redoMaster, clipsDialogOpen]);

  // ── Video playback ──
  const handleLoadedMetadata = () => {
    const d = videoRef.current.duration;
    if (d && !project.source_duration) {
      setProject((p) => ({ ...p, source_duration: d }));
      scheduleAutosave(projectRef.current.clips, { source_duration: d });
    }
  };

  const handleTimeUpdate = () => {
    const t = videoRef.current.currentTime;
 
    setCurrentTime(t);
    if (selectedClip && t >= selectedClip.out_point) {
      videoRef.current.pause();
      videoRef.current.currentTime = selectedClip.in_point;
      setIsPlaying(false);
    }
  };

  const seekTo = (t) => {
    if (videoRef.current) videoRef.current.currentTime = t;
    setCurrentTime(t);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      if (selectedClip && (currentTime < selectedClip.in_point || currentTime >= selectedClip.out_point)) {
        videoRef.current.currentTime = selectedClip.in_point;
      }
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // ── Clip mutations ──
  const addClip = () => {
    if (!project) { toast.info('Upload or select a video first to create clips'); return; }
    const defaultLen = activeOutputType === 'sf' ? 15 : Math.min(60, (project.source_duration || 60) - currentTime);
    const start = currentTime;
    const end = Math.min((project.source_duration || start + defaultLen), start + Math.max(1, defaultLen));
    const clip = {
      id: newId(), output_type: activeOutputType, name: `${activeOutputType.toUpperCase()} Clip ${clipsOfActiveType.length + 1}`,
      order: clipsOfActiveType.length, in_point: start, out_point: end, brandkit_id: null, captions: [],
      approved: false, render_job_id: null, render_status: 'idle', output_b2_key: null, render_error: null,
    };
    applyClips([...project.clips, clip]);
    setSelectedClipId(clip.id);
  };

  const updateSelectedClip = (partial) => {
    if (!selectedClip) return;
    const next = project.clips.map((c) => {
      if (c.id !== selectedClip.id) return c;
      const merged = { ...c, ...partial };
      merged.captions = clampCaptionsToDuration(merged.captions, merged.out_point - merged.in_point);
      return merged;
    });
    applyClips(next);
  };

  const renameClip = (id, name) => applyClips(project.clips.map((c) => (c.id === id ? { ...c, name } : c)));

  const removeClip = (id) => {
    applyClips(project.clips.filter((c) => c.id !== id));
    if (selectedClipId === id) setSelectedClipId(null);
  };

  const reorderClip = (id, direction) => {
    const sameType = project.clips.filter((c) => c.output_type === activeOutputType).sort((a, b) => a.order - b.order);
    const idx = sameType.findIndex((c) => c.id === id);
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= sameType.length) return;
    [sameType[idx].order, sameType[swapIdx].order] = [sameType[swapIdx].order, sameType[idx].order];
    const reindexed = sameType.sort((a, b) => a.order - b.order).map((c, i) => ({ ...c, order: i }));
    const others = project.clips.filter((c) => c.output_type !== activeOutputType);
    applyClips([...others, ...reindexed]);
  };

  const splitSelectedClip = () => {
    if (!selectedClip) return;
    const splitLocal = currentTime - selectedClip.in_point;
    const clip1Captions = [];
    const clip2Captions = [];
    selectedClip.captions.forEach((cap) => {
      if (cap.end <= splitLocal) clip1Captions.push(cap);
      else if (cap.start >= splitLocal) clip2Captions.push({ ...cap, start: cap.start - splitLocal, end: cap.end - splitLocal });
      else {
        clip1Captions.push({ ...cap, end: splitLocal });
        clip2Captions.push({ ...cap, id: newId(), start: 0, end: cap.end - splitLocal });
      }
    });
    const clip1 = { ...selectedClip, out_point: currentTime, captions: clip1Captions };
    const clip2 = {
      ...selectedClip, id: newId(), name: `${selectedClip.name} (2)`, in_point: currentTime,
      captions: clip2Captions, approved: false, render_job_id: null, render_status: 'idle', output_b2_key: null, render_error: null,
      order: selectedClip.order + 0.5,
    };
    const rest = project.clips.filter((c) => c.id !== selectedClip.id);
    const merged = [...rest, clip1, clip2]
      .sort((a, b) => (a.output_type === b.output_type ? a.order - b.order : 0))
      .map((c, i, arr) => (c.output_type === activeOutputType ? { ...c, order: arr.filter((x) => x.output_type === activeOutputType).sort((a, b) => a.order - b.order).indexOf(c) } : c));
    applyClips(merged);
    setSelectedClipId(clip1.id);
    toast.success('Clip split at playhead');
  };

  // ── Captions ──
  const addCaption = (localTime) => {
    if (!selectedClip) return;
    const duration = selectedClip.out_point - selectedClip.in_point;
    const cap = { id: newId(), start: localTime, end: Math.min(localTime + 2, duration), text: '' };
    updateSelectedClip({ captions: [...selectedClip.captions, cap].sort((a, b) => a.start - b.start) });
  };
  const updateCaption = (capId, partial) => {
    updateSelectedClip({ captions: selectedClip.captions.map((c) => (c.id === capId ? { ...c, ...partial } : c)) });
  };
  const removeCaption = (capId) => updateSelectedClip({ captions: selectedClip.captions.filter((c) => c.id !== capId) });

  // ── Brand Kit ──
  const applyBrandKit = (brandkitId) => updateSelectedClip({ brandkit_id: brandkitId });
  const applyBrandKitToAllOfType = (brandkitId) => {
    applyClips(project.clips.map((c) => (c.output_type === selectedClip.output_type ? { ...c, brandkit_id: brandkitId } : c)));
    toast.success(`Applied to all ${selectedClip.output_type.toUpperCase()} clips`);
  };

  // ── AI Best Moments ──
  const requestAiSuggest = async (numClips) => {
    if (!project) { toast.info('Upload or select a video first'); return; }
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/ai-suggest`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ num_clips: numClips }),
      });
      const data = await res.json();
      setAiLastResult(data);
      if (data.suggestions?.length) {
        const next = [...project.ai_suggestions, ...data.suggestions];
        setProject((p) => ({ ...p, ai_suggestions: next }));
        scheduleAutosave(project.clips, { ai_suggestions: next });
      }
    } catch {
      toast.error('Could not reach AI suggestion service');
    } finally {
      setAiLoading(false);
    }
  };
  const setSuggestionStatus = (id, status) => {
    const next = project.ai_suggestions.map((s) => (s.id === id ? { ...s, status } : s));
    setProject((p) => ({ ...p, ai_suggestions: next }));
    scheduleAutosave(project.clips, { ai_suggestions: next });
  };

  // ── Review & Render ──
  const toggleApprove = (id) => applyClips(project.clips.map((c) => (c.id === id ? { ...c, approved: !c.approved } : c)));

  const pollRenderStatus = useCallback((clipId) => {
    if (pollTimersRef.current[clipId]) return;
    pollTimersRef.current[clipId] = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/${clipId}/render-status`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        setProject((p) => ({ ...p, clips: p.clips.map((c) => (c.id === clipId ? { ...c, ...data.clip } : c)) }));
        if (['completed', 'failed'].includes(data.clip.render_status)) {
          clearInterval(pollTimersRef.current[clipId]);
          delete pollTimersRef.current[clipId];
          toast[data.clip.render_status === 'completed' ? 'success' : 'error'](
            data.clip.render_status === 'completed' ? `"${data.clip.name}" finished rendering` : `"${data.clip.name}" render failed`
          );
        }
      } catch { /* transient — next tick retries */ }
    }, 3000);
  }, [project]);

  const submitRender = async (clipId) => {
    await flushAutosave();
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/${clipId}/render`, { method: 'POST', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 503) {
          setGatewayUnavailable(true);
          toast.info('Rendering backend not connected yet (Vultr pending). Your edit is saved.');
        } else {
          toast.error(data.detail?.message || data.detail || 'Could not submit render');
        }
        return;
      }
      setGatewayUnavailable(false);
      setProject((p) => ({ ...p, clips: p.clips.map((c) => (c.id === clipId ? { ...c, ...data.clip } : c)) }));
      toast.success('Submitted for rendering');
      pollRenderStatus(clipId);
    } catch {
      toast.error('Network error submitting render');
    }
  };

  const downloadRendered = async (clipId) => {
    try {
      const res = await fetch(`${API_URL}/api/editor/projects/${project.id}/clips/${clipId}/download`, { credentials: 'include' });
      if (!res.ok) { toast.error('Rendered output not available yet'); return; }
      const data = await res.json();
      window.open(data.url, '_blank');
    } catch {
      toast.error('Could not fetch download link');
    }
  };

  // ── Render ──
  if (loading) {
    return <div className="min-h-screen bg-brand-bg flex items-center justify-center"><Loader2 className="h-8 w-8 text-[#3A7BFF] animate-spin" /></div>;
  }
  if (loadError) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center" data-testid="editor-load-error">
        <div className="text-center">
          <AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-3" />
          <p className="text-white font-medium mb-1">Could not open editor</p>
          <p className="text-sm text-[#8A93A6] mb-4">{loadError}</p>
          <Button variant="outline" onClick={() => navigate('/app/create/content-studio')}>Back to Media Library</Button>
        </div>
      </div>
    );
  }
  // Upload / Media Library destinations reused inside the empty-state
  // banner below — same existing routes as before, just no longer gating
  // entry into the Editor itself.
  const emptyStateUploadLink = emptyStateProjectId
    ? `/app/create/workspace/project/${emptyStateProjectId}/upload`
    : '/app/create/content-studio';
  const emptyStateLibraryLink = emptyStateProjectId
    ? `/app/create/content-studio?projectId=${emptyStateProjectId}`
    : '/app/create/content-studio';

  return (
    <div className="min-h-screen bg-brand-bg" data-testid="unified-editor-page">
      <div className="container mx-auto p-6 max-w-[1600px]">
        {/* Breadcrumb + header */}
        <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-[#8A93A6] mb-2">
              <Link to="/app/create" className="hover:text-white">Create</Link><span className="opacity-50">/</span>
              {contentSource?.project_id || emptyStateReturnTo ? (
                <>
                  <Link to="/app/create/workspace" className="hover:text-white">Workspace</Link><span className="opacity-50">/</span>
                  <Link
                    to={contentSource?.project_id ? `/app/create/workspace/project/${contentSource.project_id}` : emptyStateReturnTo}
                    className="hover:text-white"
                    data-testid="editor-back-to-project-link"
                  >
                    Project
                  </Link>
                  <span className="opacity-50">/</span>
                </>
              ) : null}
              <span className="text-[#C0C7D4]">Editor</span>
              <span className="opacity-50">/</span>
              <Link
                to={`/app/create/content-studio?${contentSource?.project_id ? `projectId=${contentSource.project_id}&` : emptyStateProjectId ? `projectId=${emptyStateProjectId}&` : ''}returnTo=${encodeURIComponent(currentSourceId ? `/app/create/editor/${currentSourceId}` : (emptyStateReturnTo || '/app/create/workspace'))}`}
                className="hover:text-white"
                data-testid="editor-media-library-link"
              >
                Media Library
              </Link>
            </div>
            <h1 className="text-2xl font-bold text-white truncate" data-testid="editor-title">{contentSource?.title || 'Unified Editor'}</h1>
            {siblingSources.length > 1 && (
              <div className="mt-2 flex items-center gap-2">
                <Video className="h-3.5 w-3.5 text-[#8A93A6]" />
                <Select value={currentSourceId} onValueChange={switchSource}>
                  <SelectTrigger className="h-7 w-56 text-xs bg-white/5 border-white/10" data-testid="editor-source-select">
                    <SelectValue placeholder="Select video" />
                  </SelectTrigger>
                  <SelectContent>
                    {siblingSources.map((s) => (
                      <SelectItem key={s.id} value={s.id} data-testid={`editor-source-option-${s.id}`}>{s.title || s.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-[#8A93A6] mx-1" data-testid="editor-save-indicator">{saving ? 'Saving…' : project ? 'Saved' : 'Draft (not saved)'}</span>
            <ProductionToolsDropdown
              currentTool="editor"
              creatorId={contentSource?.creator_id || project?.creator_id || localStorage.getItem('activeCreatorId')}
              projectId={contentSource?.project_id || emptyStateProjectId}
              assetId={currentSourceId}
              returnTo={currentSourceId ? `/app/create/editor/${currentSourceId}` : (emptyStateReturnTo || '/app/create/workspace')}
              onBeforeNavigate={flushAutosave}
            />
          </div>
        </div>

        {masterComposition?.aspect_ratio && masterComposition.aspect_ratio !== '16:9' && (
          <div className="mb-4 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3 flex items-center gap-2.5" data-testid="editor-aspect-ratio-compat-warning">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
            <p className="text-xs text-amber-200">
              This composition was previously saved at <span className="font-semibold">{masterComposition.aspect_ratio}</span>. Main Editor now standardizes on 16:9 for long-form content — your saved aspect ratio and content have not been changed; the selector is locked so new edits don't drift from it.
            </p>
          </div>
        )}

        {/* Main Editor workspace — CapCut-style layout: icon rail, expandable
           tool panel, large central preview, then the editing toolbar
           directly above the resizable multitrack timeline. This is the
           only Editor mode now — the legacy Clip Editor UI moved into the
           "Clips & Publish" dialog (state/handlers preserved, no longer
           reachable from a visible button pending the Universal Production
           Tools Menu task). */}
        <div className="space-y-3" data-testid="main-editor-workspace">
          <div className="flex gap-3 items-stretch">
            <EditorToolSidebar activeCategory={viewMode === 'properties' ? null : toolCategory} onSelectCategory={handleSelectCategory} collapsed={sidebarCollapsed} onToggleCollapsed={() => setSidebarCollapsed((c) => !c)} />

            {!sidebarCollapsed && (
              <div className="w-72 shrink-0 lg:h-[480px] overflow-y-auto" data-testid="editor-tool-panel">
                {viewMode === 'properties' && masterSelectedItem ? (
                  <div className="glass rounded-xl p-3 lg:h-[480px] flex flex-col" data-testid="editor-properties-panel">
                    <button
                      onClick={handleBackFromProperties}
                      className="flex items-center gap-1.5 text-xs text-[#8A93A6] hover:text-white mb-2 shrink-0"
                      data-testid="properties-back-button"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" />Back to {TOOL_CATEGORY_LABELS[lastCategoryBeforeProperties] || 'tools'}
                    </button>
                    <div className="overflow-y-auto flex-1 pr-1">
                      <EditorInspectorPanel
                        item={masterSelectedItem} source={masterSelectedItemSource} siblingSources={siblingSources}
                        track={masterSelectedItemTrack} allTracks={masterComposition?.tracks || []} masterComposition={masterComposition}
                        localTime={masterSelectedItem ? masterPlayhead - masterSelectedItem.timeline_start_seconds : 0}
                        onUpdateItem={updateMasterItem}
                        onTransformChange={(partial) => updateMasterItem(masterSelectedItemId, { transform: { ...(masterSelectedItem?.transform || defaultTransform()), ...partial } })}
                        onCropChange={(crop) => updateMasterItem(masterSelectedItemId, { crop })}
                        onSpeedChange={(v) => setItemSpeed(masterSelectedItemId, v)}
                        onTogglePip={() => togglePip(masterSelectedItemId)}
                        onSetReframing={(enabled, ar) => setReframing(masterSelectedItemId, enabled, ar)}
                        onAudioChange={(partial) => updateMasterItem(masterSelectedItemId, { audio: { ...(masterSelectedItem?.audio || defaultAudioProps()), ...partial } })}
                        onDetachAudio={() => detachAudio(masterSelectedItemId)}
                        onReplaceAudioSource={(newSourceId) => replaceAudioSource(masterSelectedItemId, newSourceId)}
                        onAudioProcessingChange={(partial) => setAudioProcessing(masterSelectedItemId, partial)}
                        onDuckingChange={(trackId, partial) => updateTrackDucking(trackId, partial)}
                        onAddKeyframe={() => addKeyframeAtPlayhead(masterSelectedItemId)}
                        onRemoveKeyframe={(kfId) => removeKeyframe(masterSelectedItemId, kfId)}
                        onRequestTranscription={requestTranscription}
                        textStylePresets={masterComposition?.text_style_presets || []}
                        showSafeZones={!!masterComposition?.show_safe_zones}
                        onToggleSafeZones={toggleSafeZones}
                        onDuplicateItem={() => duplicateMasterItem(masterSelectedItemId)}
                        onSaveTextPreset={saveTextStylePreset}
                      />
                    </div>
                  </div>
                ) : (
                <>
                {toolCategory === 'media' && (
                  <EditorMediaPanel
                    sources={siblingSources} parentAssetId={masterComposition?.parent_asset_id} onQuickAdd={addSourceToTimeline}
                    uploadTo={emptyStateUploadLink} libraryTo={emptyStateLibraryLink}
                    startFromVideos={emptyStateVideos} startFromLoading={emptyStateVideosLoading} onStartFromVideo={switchSource}
                  />
                )}
                {toolCategory === 'audio' && (
                  <div className="glass rounded-xl p-4 space-y-3 lg:h-[480px] overflow-y-auto" data-testid="editor-audio-panel">
                    <h3 className="text-sm font-medium text-white">Audio</h3>
                    <Button size="sm" variant="outline" className="border-white/10 w-full justify-start" onClick={() => addTrack('audio')} data-testid="audio-panel-add-track-button">
                      <Plus className="h-3.5 w-3.5 mr-1.5" />Add Audio Track
                    </Button>
                    <p className="text-[11px] uppercase tracking-wide text-[#555E6E] pt-1">Available Audio Tools</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {['Volume & Mute', 'Gain', 'Fade In/Out', 'Noise Reduction', 'Voice Enhancement', 'Track Ducking', 'Detach Audio', 'Replace Source'].map((t) => (
                        <div key={t} className="px-2 py-1.5 rounded bg-white/5 text-[10px] text-[#C0C7D4]" data-testid={`audio-tool-browse-${t.replace(/\s+/g, '-')}`}>{t}</div>
                      ))}
                    </div>
                    <EditorPlaceholderPanel
                      title="Clip audio controls"
                      body="Select an audio or video clip on the timeline to adjust these in Properties."
                    />
                  </div>
                )}
                {toolCategory === 'text' && (
                  <div className="glass rounded-xl p-4 space-y-2 lg:h-[480px]" data-testid="editor-text-panel">
                    <h3 className="text-sm font-medium text-white mb-2">Text</h3>
                    <Button size="sm" variant="outline" className="border-white/10 w-full justify-start" onClick={() => addTextItem(masterComposition?.tracks.find((t) => t.type === 'text')?.id, masterPlayhead)} data-testid="text-panel-add-title-button">
                      <Type className="h-3.5 w-3.5 mr-1.5" />Add Text Title
                    </Button>
                    <p className="text-[11px] uppercase tracking-wide text-[#555E6E] pt-2">Styles & Presets</p>
                    {(masterComposition?.text_style_presets || []).length === 0 ? (
                      <p className="text-[11px] text-[#555E6E]">No saved text presets yet — style a text title, then save it as a preset from Properties.</p>
                    ) : (
                      <div className="space-y-1">
                        {masterComposition.text_style_presets.map((p) => (
                          <div key={p.id} className="px-2 py-1.5 rounded bg-white/5 text-[11px] text-[#C0C7D4]" data-testid={`text-preset-browse-${p.id}`}>{p.name}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {toolCategory === 'captions' && (
                  <div className="glass rounded-xl p-4 space-y-3 lg:h-[480px]" data-testid="editor-captions-panel">
                    <h3 className="text-sm font-medium text-white">Captions</h3>
                    <Button size="sm" variant="outline" className="border-white/10 w-full justify-start" onClick={() => addCaptionBlock(masterComposition?.tracks.find((t) => t.type === 'text')?.id, masterPlayhead)} data-testid="captions-panel-add-block-button">
                      <Captions className="h-3.5 w-3.5 mr-1.5" />Add Caption Block
                    </Button>
                    <p className="text-[11px] uppercase tracking-wide text-[#555E6E] pt-1">Animated Caption Styles</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {['None', 'Karaoke', 'Pop Word'].map((s) => (
                        <div key={s} className="px-2 py-1.5 rounded bg-white/5 text-center text-[10px] text-[#C0C7D4]" data-testid={`caption-style-browse-${s}`}>{s}</div>
                      ))}
                    </div>
                    <EditorPlaceholderPanel
                      title="Caption editing"
                      body="Select a caption block on the timeline to edit segments, styles and auto-transcription in Properties."
                    />
                  </div>
                )}
                {toolCategory === 'effects' && (
                  <div className="glass rounded-xl p-4 lg:h-[480px] overflow-y-auto" data-testid="editor-effects-panel">
                    <EditorEffectsCatalogPanel selectedItem={masterSelectedItem} onApplyEffect={applyEffectToSelected} />
                  </div>
                )}
                {toolCategory === 'transitions' && (
                  <div className="glass rounded-xl p-4 lg:h-[480px] overflow-y-auto" data-testid="editor-transitions-panel">
                    <EditorTransitionsCatalogPanel selectedItem={masterSelectedItem} onApplyTransition={applyTransitionToSelected} />
                  </div>
                )}
                {toolCategory === 'brand' && (
                  <EditorBrandDefaults
                    effectiveBrandKit={effectiveBrandKit} brandkits={brandkits} branding={masterComposition?.branding} siblingSources={siblingSources}
                    onSetBrandKitOverride={setBrandKitOverride} onToggleWatermark={toggleWatermark} onUpdateWatermark={updateWatermark}
                    onInsertIntro={insertBrandIntro} onInsertOutro={insertBrandOutro} onGenerateBrandPresets={generateBrandPresets}
                  />
                )}
                {toolCategory === 'ai' && (
                  <EditorAiToolsPanel
                    siblingSources={siblingSources} suggestions={project?.ai_edit_suggestions || []}
                    onRequestAnalysis={requestAiAnalysis} onAcceptSuggestion={acceptAiSuggestion} onRejectSuggestion={rejectAiSuggestion}
                  />
                )}
                {toolCategory === 'templates' && (
                  <EditorTemplatesPanel creatorId={contentSource?.creator_id || project?.creator_id || localStorage.getItem('activeCreatorId')} projectId={contentSource?.project_id} episodeId={currentSourceId} />
                )}
                </>
                )}
              </div>
            )}

            <div className="flex-1 min-w-0">
                <EditorPreviewPlayer
                  composition={masterComposition} siblingSources={siblingSources}
                  playhead={masterPlayhead} setPlayhead={setMasterPlayhead}
                  isPlaying={masterIsPlaying} setIsPlaying={setMasterIsPlaying}
                  onDurationKnown={handleMasterDurationKnown}
                />
              </div>
          </div>

          <EditorEditingToolbar
            canUndo={masterHistory.length > 0} canRedo={masterFuture.length > 0}
            onUndo={undoMaster} onRedo={redoMaster}
            hasSelection={!!masterSelectedItemId} onSplit={splitMasterItem} onDelete={deleteMasterItem} onMerge={mergeWithNextItem}
            ripple={masterRipple} onToggleRipple={() => setMasterRipple((r) => !r)}
            zoom={masterZoom} onZoomChange={setMasterZoom} onFitZoom={fitMasterZoom}
            snap={masterSnap} onToggleSnap={() => setMasterSnap((s) => !s)}
            aspectRatio={masterComposition?.aspect_ratio || '16:9'} aspectRatioLocked
          />

          <div className="relative">
            <div
              className="h-2 -mb-1 flex items-center justify-center gap-2 cursor-row-resize group relative z-10"
              onMouseDown={timelineCollapsed ? undefined : startTimelineResize}
              data-testid="timeline-resize-handle"
              title={timelineCollapsed ? 'Timeline collapsed' : 'Drag to resize the timeline'}
            >
              <div className="h-1 w-16 rounded-full bg-white/10 group-hover:bg-[#3A7BFF]/60 transition-colors" />
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setTimelineCollapsed((c) => !c); }}
                className="absolute right-1 -top-1 h-5 w-5 rounded-md bg-white/5 hover:bg-white/10 text-[#8A93A6] hover:text-white flex items-center justify-center"
                title={timelineCollapsed ? 'Expand timeline' : 'Collapse timeline'}
                data-testid="timeline-collapse-toggle"
              >
                {timelineCollapsed ? <ChevronsUp className="h-3.5 w-3.5" /> : <ChevronsDown className="h-3.5 w-3.5" />}
              </button>
            </div>
            <div style={timelineCollapsed ? { display: 'none' } : undefined}>
            <EditorMultiTrackTimeline
              composition={masterComposition} siblingSources={siblingSources}
              playhead={masterPlayhead} setPlayhead={setMasterPlayhead}
              selectedItemId={masterSelectedItemId} onSelectItem={setMasterSelectedItemId}
              zoom={masterZoom} snap={masterSnap}
              onDropNewItem={dropSourceOnTimeline} onMoveItem={moveMasterItem} onTrimItem={trimMasterItem}
              onToggleTrackFlag={toggleTrackFlag} onAddTextItem={addTextItem} onWaveformReady={cacheWaveform}
              onAddTrack={addTrack} onRemoveTrack={removeTrack} tracksHeight={tracksHeight}
              markers={markers} onAddMarker={addMarker} onRenameMarker={renameMarker} onMoveMarker={moveMarker} onRemoveMarker={removeMarker}
              scrollRef={masterTimelineScrollRef}
            />
            </div>
          </div>
        </div>

        {/* ── Clip Editor (existing LF/SF workflow) — legacy clips unchanged; Master-derived clips (Stage 3E) get a composition-aware preview + Master-range retrim instead of a single-source video/trim; now opens in a dialog from the "Clips & Publish" header button instead of a competing visible mode ── */}
        <Dialog open={clipsDialogOpen} onOpenChange={setClipsDialogOpen}>
        <DialogContent className="max-w-[1400px] w-[95vw] max-h-[92vh] overflow-y-auto bg-[#0C0E12] border-white/10" data-testid="clips-publish-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-4 w-4" />Clips & Publish
            <Button size="icon" variant="outline" className="border-white/10 h-7 w-7 ml-2" disabled={history.length === 0} onClick={undo} data-testid="undo-button"><Undo2 className="h-3.5 w-3.5" /></Button>
            <Button size="icon" variant="outline" className="border-white/10 h-7 w-7" disabled={future.length === 0} onClick={redo} data-testid="redo-button"><Redo2 className="h-3.5 w-3.5" /></Button>
          </DialogTitle>
        </DialogHeader>
        {
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Preview + Timeline */}
          <div className="xl:col-span-2 space-y-4">
            {selectedClip?.master_composition_slice ? (
              <div data-testid="clip-studio-preview">
                <EditorPreviewPlayer
                  composition={selectedClip.master_composition_slice} siblingSources={siblingSources}
                  playhead={currentTime} setPlayhead={setCurrentTime}
                  isPlaying={isPlaying} setIsPlaying={setIsPlaying}
                  onDurationKnown={() => {}}
                />
              </div>
            ) : (
            <div className="relative aspect-video bg-black rounded-xl overflow-hidden border border-white/10" data-testid="editor-preview-player">
              {streamUrl ? (
                <video
                  ref={videoRef} src={streamUrl} className="w-full h-full object-contain"
                  onLoadedMetadata={handleLoadedMetadata} onTimeUpdate={handleTimeUpdate}
                  onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)}
                  data-testid="editor-video-element"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-center p-6" data-testid="editor-preview-unavailable">
                  <div>
                    <Film className="h-10 w-10 text-[#555E6E] mx-auto mb-2" />
                    <p className="text-sm text-[#8A93A6]">{streamError || 'Preview unavailable'}</p>
                  </div>
                </div>
              )}

              {activeCaption && (
                <div className="absolute bottom-10 left-0 right-0 text-center pointer-events-none" data-testid="editor-caption-overlay">
                  <span className="bg-black/70 text-white text-sm px-3 py-1 rounded-md inline-block max-w-[90%]">{activeCaption.text}</span>
                </div>
              )}
              {selectedBrandkit && selectedClip && (
                <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-white/10" data-testid="editor-brandkit-progress-preview">
                  <div
                    className="h-full"
                    style={{
                      background: selectedBrandkit.primary_color,
                      width: `${Math.min(100, Math.max(0, ((currentTime - selectedClip.in_point) / (selectedClip.out_point - selectedClip.in_point)) * 100))}%`,
                    }}
                  />
                </div>
              )}
              <Badge className="absolute top-3 left-3 bg-black/60 text-white border-0" data-testid="editor-preview-badge">PREVIEW</Badge>

              {streamUrl && (
                <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center group" data-testid="editor-play-pause-button">
                  <span className="h-14 w-14 rounded-full bg-black/40 group-hover:bg-black/60 flex items-center justify-center transition-colors">
                    {isPlaying ? <Pause className="h-6 w-6 text-white" /> : <Play className="h-6 w-6 text-white ml-0.5" />}
                  </span>
                </button>
              )}
            </div>
            )}

            <Tabs value={activeOutputType} onValueChange={(v) => { setActiveOutputType(v); setSelectedClipId(null); }}>
              <TabsList className="glass" data-testid="output-type-tabs">
                <TabsTrigger value="lf" className="flex items-center gap-2" data-testid="output-type-lf"><Film className="h-4 w-4" />Long Form</TabsTrigger>
                <TabsTrigger value="sf" className="flex items-center gap-2" data-testid="output-type-sf"><Smartphone className="h-4 w-4" />Short Form</TabsTrigger>
              </TabsList>
            </Tabs>

            <EditorTimelineRuler
              duration={effectiveDuration}
              currentTime={currentTime}
              clips={clipsOfActiveType}
              selectedClipId={selectedClipId}
              aiMarkers={project?.ai_suggestions}
              onSeek={seekTo}
              onSelectClip={setSelectedClipId}
            />

            <div className="glass rounded-xl p-4">
              {selectedClip?.master_composition_slice ? (
                <div className="space-y-3" data-testid="clip-master-range-editor">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-white font-medium flex items-center gap-1.5">
                      <Clapperboard className="h-3.5 w-3.5 text-[#3A7BFF]" />Master Range · from v{selectedClip.source_master_version}
                    </p>
                    <Badge className="bg-white/10 text-[#8A93A6] border-0 text-[10px]" data-testid="clip-origin-badge">
                      {selectedClip.generated_from === 'ai_suggestion' ? 'AI Generated' : 'Manual'}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-[#8A93A6] mb-1 block">Master start (s)</label>
                      <Input
                        type="number" step="0.1" value={clipRangeDraft.start}
                        onChange={(e) => setClipRangeDraft((d) => ({ ...d, start: Number(e.target.value) }))}
                        className="bg-[#1C1F26] border-white/10 text-white" data-testid="clip-range-start-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-[#8A93A6] mb-1 block">Master end (s)</label>
                      <Input
                        type="number" step="0.1" value={clipRangeDraft.end}
                        onChange={(e) => setClipRangeDraft((d) => ({ ...d, end: Number(e.target.value) }))}
                        className="bg-[#1C1F26] border-white/10 text-white" data-testid="clip-range-end-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-[#8A93A6] shrink-0">Aspect ratio</label>
                    <Select
                      value={selectedClip.target_aspect_ratio || 'inherit'}
                      onValueChange={(v) => retrimMasterClip(selectedClip.id, clipRangeDraft.start, clipRangeDraft.end, v === 'inherit' ? null : v)}
                    >
                      <SelectTrigger className="h-7 w-36 text-xs bg-white/5 border-white/10" data-testid="clip-aspect-ratio-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">Inherit Master</SelectItem>
                        {['16:9', '9:16', '1:1', '4:5'].map((ar) => <SelectItem key={ar} value={ar}>{ar}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    size="sm" className="w-full bg-[#3A7BFF] hover:bg-[#3A7BFF]/90"
                    onClick={() => retrimMasterClip(selectedClip.id, clipRangeDraft.start, clipRangeDraft.end, selectedClip.target_aspect_ratio)}
                    data-testid="apply-master-range-button"
                  >
                    Apply Range
                  </Button>
                  <p className="text-[10px] text-[#555E6E]">Re-cuts this clip from the exact Master version it was generated from — never alters the Master or any other clip.</p>
                </div>
              ) : (
                <EditorClipTrimControls clip={selectedClip} duration={effectiveDuration} currentTime={currentTime} onChange={updateSelectedClip} onSplit={splitSelectedClip} />
              )}
            </div>
          </div>

          {/* Right panel */}
          <div className="glass rounded-xl p-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="glass w-full grid grid-cols-7 mb-4" data-testid="editor-right-tabs">
                <TabsTrigger value="approve" data-testid="tab-approve-panel"><ShieldCheck className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="clips" data-testid="tab-clips-panel"><Scissors className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="captions" data-testid="tab-captions-panel"><Captions className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="brandkit" data-testid="tab-brandkit-panel"><Palette className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="ai" data-testid="tab-ai-panel"><Sparkles className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="review" data-testid="tab-review-panel"><ClipboardCheck className="h-4 w-4" /></TabsTrigger>
                <TabsTrigger value="queue" data-testid="tab-queue-panel"><ListChecks className="h-4 w-4" /></TabsTrigger>
              </TabsList>

              <TabsContent value="approve" className="space-y-4">
                <EditorMasterApprovalPanel
                  approvalStatus={masterComposition?.approval_status}
                  currentApprovedVersion={masterComposition?.current_approved_version}
                  activeSnapshot={activeApprovalSnapshot}
                  versions={masterVersions}
                  onApprove={approveMaster}
                  onRevoke={revokeMasterApproval}
                  approving={approvingMaster}
                />
                <EditorClipGenerationPanel
                  disabled={masterComposition?.approval_status !== 'approved'}
                  approvedDuration={activeApprovalSnapshot?.composition_snapshot?.duration_seconds}
                  onGenerate={generateClips}
                  generating={generatingClips}
                  acceptedHighlightSuggestions={(project?.ai_edit_suggestions || []).filter((s) => s.kind === 'highlight' && s.status === 'accepted')}
                  onGenerateFromSuggestions={generateClipsFromSuggestions}
                  markers={markers}
                />
              </TabsContent>

              <TabsContent value="clips">
                <EditorClipList clips={clipsOfActiveType} selectedClipId={selectedClipId} onSelect={setSelectedClipId} onAdd={addClip} onRename={renameClip} onRemove={removeClip} onReorder={reorderClip} />
              </TabsContent>
              <TabsContent value="captions">
                <EditorCaptionsPanel
                  captions={selectedClip?.captions}
                  duration={selectedClip ? selectedClip.out_point - selectedClip.in_point : 0}
                  currentTime={selectedClip ? currentTime - selectedClip.in_point : 0}
                  onAdd={addCaption} onUpdate={updateCaption} onRemove={removeCaption}
                />
              </TabsContent>
              <TabsContent value="brandkit">
                <EditorBrandKitPanel clip={selectedClip} brandkits={brandkits} defaultBrandKitId={effectiveBrandKit?.id} onApply={applyBrandKit} onApplyToAllOfType={applyBrandKitToAllOfType} />
              </TabsContent>
              <TabsContent value="ai">
                <EditorAiPanel suggestions={project?.ai_suggestions} loading={aiLoading} lastResult={aiLastResult} onRequest={requestAiSuggest} onAccept={(id) => setSuggestionStatus(id, 'accepted')} onReject={(id) => setSuggestionStatus(id, 'rejected')} />
              </TabsContent>
              <TabsContent value="review">
                <EditorReviewPanel clips={allClipsSorted} onToggleApprove={toggleApprove} onSubmitRender={submitRender} onDownload={downloadRendered} gatewayUnavailable={gatewayUnavailable} />
              </TabsContent>
              <TabsContent value="queue">
                <EditorClipReviewQueue
                  clips={allClipsSorted} onBatchApprove={batchApproveClips}
                  onSelectClip={(id) => { setSelectedClipId(id); const c = project?.clips.find((x) => x.id === id); if (c) setActiveOutputType(c.output_type); setActiveTab('clips'); }}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
        }
        </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
